import { useEffect } from "react";
import {
  Wheat,
  Shrimp,
  Egg,
  Fish,
  Nut,
  Bean,
  Milk,
  Leaf,
  Droplet,
  Flower2,
  FlaskConical,
  Sprout,
  Shell,
  AlertTriangle,
  ArrowRight,
} from "lucide-react";
import { Reveal } from "@/components/Reveal";

const ALLERGENS = [
  { icon: Wheat, name: "Gluten (trigo)", id: "gluten" },
  { icon: Shrimp, name: "Crustáceos", id: "crustaceos" },
  { icon: Egg, name: "Huevos", id: "huevos" },
  { icon: Fish, name: "Pescado", id: "pescado" },
  { icon: Nut, name: "Cacahuetes", id: "cacahuetes" },
  { icon: Bean, name: "Soja", id: "soja" },
  { icon: Milk, name: "Leche (lácteos)", id: "leche" },
  { icon: Nut, name: "Frutos de cáscara", id: "frutos-cascara" },
  { icon: Leaf, name: "Apio", id: "apio" },
  { icon: Droplet, name: "Mostaza", id: "mostaza" },
  { icon: Flower2, name: "Sésamo", id: "sesamo" },
  { icon: FlaskConical, name: "Sulfitos", id: "sulfitos" },
  { icon: Sprout, name: "Altramuces", id: "altramuces" },
  { icon: Shell, name: "Moluscos", id: "moluscos" },
];

const Alergenos = () => {
  useEffect(() => {
    document.title = "Información sobre Alérgenos | CROC LAB Valencia";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute(
        "content",
        "Información sobre los 14 principales alérgenos en CROC LAB Valencia. Consulta nuestro aviso sobre contacto cruzado antes de realizar tu pedido."
      );
    }
  }, []);

  return (
    <main data-testid="alergenos-page" className="py-14 md:py-20">
      <div className="max-w-5xl mx-auto px-6 lg:px-8">
        <a
          href="/"
          data-testid="alergenos-back-link"
          className="text-sm font-semibold text-brand-pink hover:underline"
        >
          ← Volver al inicio
        </a>
        <Reveal>
          <h1 className="mt-6 font-display font-bold tracking-tight text-brand-charcoal text-3xl md:text-4xl">
            INFORMACIÓN SOBRE ALÉRGENOS
          </h1>
          <p className="mt-2 text-sm text-brand-charcoal/50">
            Última actualización: agosto 2026
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <section
            data-testid="alergenos-aviso"
            aria-labelledby="alergenos-aviso-titulo"
            className="mt-10 bg-brand-charcoal text-white rounded-3xl p-6 md:p-10"
          >
            <h2
              id="alergenos-aviso-titulo"
              className="flex items-center gap-3 font-display font-semibold text-xl md:text-2xl"
            >
              <span className="w-11 h-11 rounded-full bg-brand-pink/15 text-brand-pink flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5" aria-hidden="true" />
              </span>
              AVISO IMPORTANTE
            </h2>
            <p className="mt-4 text-sm md:text-base text-white/80 leading-relaxed">
              Todos nuestros alimentos se preparan y manipulan en espacios donde
              pueden estar presentes distintos alérgenos. Por este motivo, es
              posible el contacto cruzado entre ingredientes.
            </p>
            <p className="mt-3 text-sm md:text-base text-white/80 leading-relaxed">
              Si tienes alguna alergia o intolerancia alimentaria, especialmente
              si es grave, te recomendamos que consultes directamente con
              nuestro equipo antes de realizar tu pedido.
            </p>
          </section>
        </Reveal>

        <section aria-labelledby="alergenos-lista-titulo" className="mt-14">
          <Reveal>
            <h2
              id="alergenos-lista-titulo"
              className="font-display font-bold tracking-tight text-brand-charcoal text-2xl md:text-3xl"
            >
              LOS 14 PRINCIPALES ALÉRGENOS
            </h2>
            <p className="mt-3 text-sm md:text-base text-brand-charcoal/75 leading-relaxed max-w-2xl">
              Nuestros productos pueden contener o entrar en contacto con los
              siguientes alérgenos:
            </p>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {ALLERGENS.map((allergen, i) => (
              <Reveal key={allergen.id} delay={Math.min(i * 0.03, 0.3)}>
                <div
                  data-testid={`alergeno-box-${allergen.id}`}
                  className="flex items-center gap-4 bg-white rounded-2xl border border-brand-charcoal/10 shadow-sm px-5 py-4 hover:shadow-md hover:-translate-y-0.5 transition-all duration-300"
                >
                  <span className="w-11 h-11 rounded-full bg-brand-pink/10 text-brand-pink flex items-center justify-center shrink-0">
                    <allergen.icon className="w-5 h-5" aria-hidden="true" />
                  </span>
                  <p className="font-semibold text-brand-charcoal text-sm md:text-base">
                    {allergen.name}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <p className="mt-8 text-xs md:text-sm text-brand-charcoal/50 leading-relaxed max-w-2xl">
              Esta lista recoge las 14 categorías de alérgenos reguladas. No
              todos nuestros productos contienen todos estos alérgenos. La
              información detallada producto a producto estará disponible junto
              a la carta final.
            </p>
            <a
              data-testid="alergenos-contacto-link"
              href="/#contacto"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-pink hover:underline"
            >
              ¿Dudas sobre alérgenos? Escríbenos
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </a>
          </Reveal>
        </section>
      </div>
    </main>
  );
};

export default Alergenos;
